import { useState, type ReactNode } from 'react';
import type { WaitForTriggerFunction } from '../hooks/use-wait-for-trigger';
import { type JSX } from 'react/jsx-runtime';
import { useLayoutEffect } from '../use-isomorphic-layout-effect';

type ValidatedState = 'pending' | 'allowed' | 'blocked';

/** Hide content until validated; avoids flash without off-screen positioning or scrollbar issues. */
const HIDDEN_CONTAINER_STYLE: React.CSSProperties = {
	display: 'none',
};

/**
 * Validates that waitForTrigger was called for triggered messages.
 *
 * - First render: render children inside a hidden container so the template runs and can call
 *   waitForTrigger without any visible flash (blocking during render would create a catch-22).
 * - After commit (useLayoutEffect): if the template did not call waitForTrigger, set state to
 *   'blocked' and return null. If it did, set 'allowed' and make the container visible.
 *
 * The effect depends on [waitForTrigger, children] so that when the template is lazy-loaded
 * (inside Suspense), we re-run after the lazy chunk resolves and the template runs. On the first
 * run we do not block if the ref is false, so a suspending template gets one more commit to
 * call waitForTrigger before we block.
 */
export const TriggeredMessageValidator = ({
	children,
	waitForTrigger,
}: {
	children: ReactNode;
	waitForTrigger?: WaitForTriggerFunction;
}): JSX.Element | null => {
	const [validated, setValidated] = useState<ValidatedState>('pending');

	useLayoutEffect(() => {
		const calledRef = waitForTrigger?.__calledRef;

		if (calledRef?.current) {
			setValidated('allowed');
			return;
		}

		// Ref not set after template had a chance to run.
		if (calledRef) {
			if (process.env.NODE_ENV !== 'production') {
				// eslint-disable-next-line no-console
				console.error(
					'[Post Office] Triggered message rendered without calling waitForTrigger. ' +
						'This message is client-triggered but the component did not call waitForTrigger. ' +
						'Please wrap your content: return waitForTrigger({ isTriggerActive: condition, messageContent: <YourContent />, onMessageShown });',
				);
			}
			setValidated('blocked');
		}
	}, [waitForTrigger, children]);

	if (validated === 'blocked') {
		return null;
	}

	const isHidden = validated === 'pending';

	return (
		<span
			style={isHidden ? HIDDEN_CONTAINER_STYLE : undefined}
			aria-hidden={isHidden}
			data-testid={isHidden ? 'triggered-message-validator-hidden' : undefined}
		>
			{children}
		</span>
	);
};
